/**
 * The URLs the homework loop's pages hand each other, built in one place so
 * the homework page, the class page links and the Save as PDF preview agree
 * on the parameter names (`class`, `homework`, `from`, `open`, `new`).
 *
 * The homework page and the preview are each reached from two surfaces, so
 * their back link comes from an explicit, validated origin (`from=homework`
 * or `from=students` with the class and homework in view), never from who
 * the viewer is. `?new=<classCodeId>` opens the homework page's New homework
 * dialog on that class, for the class page's "Assign homework".
 */

import {
  DEFAULT_TEACHER_STUDENTS_WINDOW,
  isClassPageWindow,
} from '@shared/teacherStudentsWindows';
import { TEACHER_PAGE_RETURN_STATE } from '@/utils/teacherEventSource';

const OBJECT_ID = /^[a-f\d]{24}$/i;

/** A go-over window worth carrying: one of the class page's, not its default. */
const carriedWindow = (window: string | null | undefined): string | null =>
  isClassPageWindow(window) && window !== DEFAULT_TEACHER_STUDENTS_WINDOW
    ? window
    : null;

/** The class page with this homework in view (and a go-over window, if any). */
export function classPageHref(
  classCodeId: string,
  homeworkId: string,
  window?: string | null
): string {
  const query = new URLSearchParams({
    class: classCodeId,
    homework: homeworkId,
  });
  const carried = carriedWindow(window);
  if (carried) query.set('window', carried);
  return `/teacher/students?${query.toString()}`;
}

/** The class page a hop came from: the class, its homework in view, its window. */
export interface StudentsOrigin {
  classCodeId: string;
  /** The homework in view on the class page (not necessarily the one opened). */
  homeworkId: string;
  /** The class page's go-over window, carried home. */
  window?: string | null;
}

/** `from=students&class=&homework=&window=`, appended to a page's own query. */
function appendStudentsOrigin(
  query: URLSearchParams,
  origin: StudentsOrigin
): void {
  query.set('from', 'students');
  query.set('class', origin.classCodeId);
  query.set('homework', origin.homeworkId);
  const carried = carriedWindow(origin.window);
  if (carried) query.set('window', carried);
}

/**
 * The homework page, opened on this homework. With an origin, the page's
 * back affordance returns to that class page instead of the homework list.
 */
export function homeworkPageHref(
  homeworkId: string,
  origin?: StudentsOrigin | null
): string {
  const query = new URLSearchParams({ open: homeworkId });
  if (origin) appendStudentsOrigin(query, origin);
  return `/teacher/homework?${query.toString()}`;
}

/** The homework page with its New homework dialog open on this class. */
export function newHomeworkHref(classCodeId: string): string {
  return `/teacher/homework?${new URLSearchParams({ new: classCodeId }).toString()}`;
}

/**
 * The New homework dialog on this class, "For" set to one student, with the
 * builder's back link returning to that student's page.
 */
export function newStudentHomeworkHref(
  classCodeId: string,
  studentUserId: string
): string {
  const query = new URLSearchParams({
    new: classCodeId,
    student: studentUserId,
    from: 'student',
  });
  return `/teacher/homework?${query.toString()}`;
}

/** Where the Save as PDF preview was opened from. */
export type HomeworkPrintOrigin =
  | { from: 'homework' }
  | ({ from: 'students' } & StudentsOrigin);

/** The Save as PDF preview of one homework's questions. */
export function homeworkPrintHref(
  homeworkId: string,
  origin: HomeworkPrintOrigin
): string {
  const query = new URLSearchParams();
  if (origin.from === 'students') appendStudentsOrigin(query, origin);
  else query.set('from', 'homework');
  return `/teacher/homework/${encodeURIComponent(homeworkId)}/print?${query.toString()}`;
}

export interface ClassPageReturn {
  to: string;
  /** Router state: back to the class page is a return, not a new open. */
  state: typeof TEACHER_PAGE_RETURN_STATE;
}

/**
 * The class page a `from=students` origin returns to, or null when there is
 * no origin or it is malformed (a stale or hand-edited link): only a
 * well-formed class and homework in view earn a "Back to class".
 */
export function classPageReturn(
  search: URLSearchParams
): ClassPageReturn | null {
  if (search.get('from') !== 'students') return null;
  const classCodeId = search.get('class') ?? '';
  const inView = search.get('homework') ?? '';
  if (!OBJECT_ID.test(classCodeId) || !OBJECT_ID.test(inView)) return null;
  return {
    to: classPageHref(classCodeId, inView, search.get('window')),
    state: TEACHER_PAGE_RETURN_STATE,
  };
}

export interface HomeworkPrintBackLink {
  label: 'Back to class' | 'Back to homework';
  to: string;
  state?: typeof TEACHER_PAGE_RETURN_STATE;
}

/**
 * The preview's back link: the class page when the origin is a well-formed
 * `from=students`, otherwise the homework itself.
 */
export function homeworkPrintBackLink(
  homeworkId: string,
  search: URLSearchParams
): HomeworkPrintBackLink {
  const classPage = classPageReturn(search);
  if (classPage) return { label: 'Back to class', ...classPage };
  return { label: 'Back to homework', to: homeworkPageHref(homeworkId) };
}
